import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@mui/material';
import { FaClock, FaArrowRight, FaArrowLeft, FaCheck, FaTimes } from 'react-icons/fa';

// Exam configuration
const EXAM_CONFIG = {
  sections: [
    {
      id: 1,
      name: 'פרק 1',
      timeLimit: 20 * 60, // 20 minutes
      description: 'השלמת משפטים, ניסוח מחדש והבנת הנקרא'
    },
    {
      id: 2,
      name: 'פרק 2',
      timeLimit: 20 * 60, // 20 minutes
      description: 'השלמת משפטים, ניסוח מחדש והבנת הנקרא'
    },
    {
      id: 3,
      name: 'פרק 3',
      timeLimit: 15 * 60, // 15 minutes
      description: 'חיבור'
    }
  ]
};

// Admin exam configuration (30 seconds per section)
const ADMIN_EXAM_CONFIG = {
  sections: [
    {
      id: 1,
      name: 'פרק 1',
      timeLimit: 10, // 10 seconds
      description: 'השלמת משפטים, ניסוח מחדש והבנת הנקרא'
    },
    {
      id: 2,
      name: 'פרק 2',
      timeLimit: 10, // 10 seconds
      description: 'השלמת משפטים, ניסוח מחדש והבנת הנקרא'
    },
    {
      id: 3,
      name: 'פרק 3',
      timeLimit: 10, // 10 seconds
      description: 'חיבור'
    }
  ]
};

// Helper function to format time
const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const LEVELS = [
  { key: 'easy', label: 'سهل' },
  { key: 'middle', label: 'متوسط' },
  { key: 'hard', label: 'صعب' },
];

export default function Exam() {
  // Get exam configuration based on user role
  const getExamConfig = () => {
    return user && user.role === 'admin' ? ADMIN_EXAM_CONFIG : EXAM_CONFIG;
  };

  // Exam state
  const [currentSection, setCurrentSection] = useState(0);
  const [sectionTimer, setSectionTimer] = useState(EXAM_CONFIG.sections[0].timeLimit);
  const [timerActive, setTimerActive] = useState(false);
  const [examStarted, setExamStarted] = useState(false);
  const [examCompleted, setExamCompleted] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState('middle');
  
  // Answers state
  const [answers, setAnswers] = useState({
    section1: {
      completion: Array(8).fill(null),
      rephrase: Array(5).fill(null),
      articles: [
        { questions: Array(4).fill(null) }, // Article 1
        { questions: Array(5).fill(null) }  // Article 2
      ]
    },
    section2: {
      completion: Array(8).fill(null),
      rephrase: Array(5).fill(null),
      articles: [
        { questions: Array(4).fill(null) }, // Article 1
        { questions: Array(5).fill(null) }  // Article 2
      ]
    },
    section3: {
      essay: ''
    }
  });

  // Questions state
  const [questions, setQuestions] = useState({
    section1: { completion: [], rephrase: [], articles: [] },
    section2: { completion: [], rephrase: [], articles: [] },
    section3: { essay: null }
  });

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Results state
  const [results, setResults] = useState(null);
  const [sectionEndMessage, setSectionEndMessage] = useState('');

  // Timer ref
  const timerRef = useRef();

  // Load exam questions when component mounts or level changes
  useEffect(() => {
    if (examStarted) {
      loadExamQuestions();
    }
    // eslint-disable-next-line
  }, [selectedLevel, examStarted]);

  // Timer effect
  useEffect(() => {
    if (timerActive && sectionTimer > 0) {
      timerRef.current = setInterval(() => {
        setSectionTimer(prev => {
          if (prev <= 1) {
            // Time's up - auto advance to next section
            handleSectionComplete();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }

    return () => clearInterval(timerRef.current);
  }, [timerActive, sectionTimer]);

  const loadExamQuestions = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load questions for all sections, passing level
      const [section1Data, section2Data, section3Data] = await Promise.all([
        fetch(process.env.REACT_APP_API_URL + `/api/exam-questions?section=1&level=${selectedLevel}`).then(res => res.json()),
        fetch(process.env.REACT_APP_API_URL + `/api/exam-questions?section=2&level=${selectedLevel}`).then(res => res.json()),
        fetch(process.env.REACT_APP_API_URL + `/api/exam-questions?section=3&level=${selectedLevel}`).then(res => res.json())
      ]);

      setQuestions({
        section1: section1Data,
        section2: section2Data,
        section3: section3Data
      });
    } catch (err) {
      setError('حدث خطأ أثناء تحميل أسئلة الامتحان');
      console.error('Error loading exam questions:', err);
    } finally {
      setLoading(false);
    }
  };

  const startExam = async () => {
    setExamStarted(true);
    setTimerActive(true);
    // Update timer with correct configuration
    setSectionTimer(getExamConfig().sections[0].timeLimit);
    try {
      await fetch(process.env.REACT_APP_API_URL + '/api/exam-start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ level: selectedLevel })
      });
    } catch {}
  };

  const handleSectionComplete = () => {
    setTimerActive(false);
    // No subscription restrictions - all users get full access
    const examConfig = getExamConfig();
    if (currentSection < examConfig.sections.length - 1) {
      setSectionEndMessage('انتهى وقت هذا القسم. سيتم الانتقال إلى القسم التالي.');
      setTimeout(() => {
        setSectionEndMessage('');
        // Move to next section
        const nextSection = currentSection + 1;
        setCurrentSection(nextSection);
        setSectionTimer(examConfig.sections[nextSection].timeLimit);
        setTimeout(() => {
          setTimerActive(true);
        }, 1000);
      }, 1500);
    } else {
      setSectionEndMessage('انتهى وقت هذا القسم. سيتم إرسال الامتحان تلقائيًا.');
      setTimeout(() => {
        setSectionEndMessage('');
        setExamCompleted(true);
        submitExam();
      }, 1500);
    }
  };

  const handleAnswerChange = (sectionType, questionIndex, answer) => {
    setAnswers(prev => {
      const newAnswers = { ...prev };
      const sectionKey = `section${currentSection + 1}`;
      
      if (sectionType === 'completion') {
        newAnswers[sectionKey].completion[questionIndex] = answer;
      } else if (sectionType === 'rephrase') {
        newAnswers[sectionKey].rephrase[questionIndex] = answer;
      } else if (sectionType === 'article') {
        const [articleIndex, questionIdx] = questionIndex.split('-').map(Number);
        newAnswers[sectionKey].articles[articleIndex].questions[questionIdx] = answer;
      } else if (sectionType === 'essay') {
        newAnswers[sectionKey].essay = answer;
      }
      
      return newAnswers;
    });
  };

  const submitExam = async () => {
    try {
      const response = await fetch(process.env.REACT_APP_API_URL + '/api/exam-submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ answers })
      });
      
      if (response.ok) {
        // Get correct answers for results display
        const corrects = {};
        // Section 1 & 2
        ['section1', 'section2'].forEach((sectionKey, sIdx) => {
          const sectionQuestions = questions[sectionKey];
          corrects[sectionKey] = {
            completion: sectionQuestions.completion?.map(q => q.correct),
            rephrase: sectionQuestions.rephrase?.map(q => q.correct),
            articles: sectionQuestions.articles?.map(article =>
              article.questions?.map(q => q.correct)
            )
          };
        });
        setResults({
          answers,
          corrects,
          essayTopic: questions.section3.essay?.topic
        });
      } else {
        throw new Error('فشل في إرسال الامتحان');
      }
    } catch (err) {
      setError('حدث خطأ أثناء إرسال الامتحان');
      console.error('Error submitting exam:', err);
    }
  };

  // Load user info
  const [user, setUser] = useState(null);
  const [premiumModal, setPremiumModal] = useState(false);
  
  // Get user type for different messaging
  const getUserType = (user) => {
    if (!user) return 'none';
    
    const isPremiumActive = user.premium && user.premiumUntil && new Date(user.premiumUntil) > new Date();
    
    if (!isPremiumActive) return 'non-premium';
    
    if (user.hasReceivedFreeTrial) {
      return 'free-trial';
    } else {
      return 'full-premium';
    }
  };
  
  const userType = getUserType(user);
  
  const getPremiumMessage = () => {
    switch (userType) {
      case 'free-trial':
        return 'يمكنك حل القسم الأول فقط في النسخة التجريبية المجانية. اشترك الآن للاستفادة من جميع الميزات.';
      case 'non-premium':
        return 'يمكنك حل القسم الأول فقط في النسخة المجانية. اشترك الآن للاستفادة من جميع الميزات.';
      default:
        return 'يمكنك حل القسم الأول فقط في النسخة المجانية. اشترك الآن للاستفادة من جميع الميزات.';
    }
  };
  
  const premiumExamMessage = getPremiumMessage();

  useEffect(() => {
    fetch(process.env.REACT_APP_API_URL + '/api/user', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        setUser(data.user);
        // Update timer with correct configuration when user loads
        if (data.user && data.user.role === 'admin') {
          setSectionTimer(ADMIN_EXAM_CONFIG.sections[0].timeLimit);
        }
      });
  }, []);

  // Render exam start screen
  if (!examStarted) {
    return (
      <div style={{ direction: 'rtl', padding: '2rem', maxWidth: 800, margin: '0 auto' }}>
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <h1 style={{ fontSize: 32, color: '#1a237e', fontWeight: 'bold', marginBottom: '1rem' }}>
              امتحان الماستر
            </h1>
            <p style={{ fontSize: 18, color: '#666', marginBottom: '2rem' }}>
              امتحان شامل يتكون من ثلاثة أقسام
            </p>
            {/* Level selector */}
            <div style={{ marginBottom: '2rem' }}>
              <span style={{ fontWeight: 'bold', marginLeft: 12 }}>اختر مستوى الامتحان:</span>
              {LEVELS.map(lvl => (
                <label key={lvl.key} style={{ margin: '0 0.5rem', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="exam-level"
                    value={lvl.key}
                    checked={selectedLevel === lvl.key}
                    onChange={() => setSelectedLevel(lvl.key)}
                    style={{ marginLeft: 4 }}
                  />
                  {lvl.label}
                </label>
              ))}
            </div>
            
            <div style={{ marginBottom: '2rem' }}>
              {getExamConfig().sections.map((section, index) => (
                <div key={section.id} style={{ 
                  marginBottom: '1rem', 
                  padding: '1rem', 
                  border: '1px solid #ddd', 
                  borderRadius: 8,
                  background: '#f9f9f9'
                }}>
                  <h3 style={{ color: '#1976d2', marginBottom: '0.5rem' }}>{section.name}</h3>
                  <p style={{ color: '#666', marginBottom: '0.5rem' }}>{section.description}</p>
                  <p style={{ color: '#f59e42', fontWeight: 'bold' }}>
                    הזמן המוקצב: {section.timeLimit < 60 ? `${section.timeLimit} שניות` : `${Math.floor(section.timeLimit / 60)} דקות`}
                  </p>
                </div>
              ))}
            </div>

            <div style={{ marginBottom: '2rem', padding: '1rem', background: '#fff3cd', borderRadius: 8 }}>
              <h4 style={{ color: '#856404', marginBottom: '0.5rem' }}>הנחיות חשובות:</h4>
              <ul style={{ textAlign: 'right', color: '#856404' }}>
                <li>לא ניתן לעבור לחלק הבא לפני תום הזמן.</li>
                <li>בתום הזמן תעבור המערכת באופן אוטומטי לחלק הבא.</li>
                <li>יש לענות על כל השאלות לפני סיום הזמן.</li>
                <li>לאחר המעבר לחלק הבא לא ניתן לחזור לחלק הקודם.</li>
              </ul>
            </div>

            <button
              onClick={startExam}
              style={{
                padding: '1rem 2rem',
                fontSize: 18,
                fontWeight: 'bold',
                background: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer'
              }}
            >
              بدء الامتحان
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render exam completion/results screen
  if (examCompleted && results) {
    return (
      <div style={{ direction: 'rtl', padding: '2rem', maxWidth: 900, margin: '0 auto' }}>
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <h1 style={{ fontSize: 32, color: '#22c55e', fontWeight: 'bold', marginBottom: '1rem' }}>
              انتهى الامتحان!
            </h1>
            <p style={{ fontSize: 18, color: '#666', marginBottom: '2rem' }}>
              تم إرسال الامتحان تلقائيًا. هذه نتائجك:
            </p>
            {/* Section 1 & 2 Results */}
            {['section1', 'section2'].map((sectionKey, sIdx) => (
              <div key={sectionKey} style={{ marginBottom: 32, textAlign: 'right' }}>
                <h2 style={{ color: '#1976d2', fontSize: 22, marginBottom: 8 }}>
                  {sIdx === 0 ? 'القسم الأول' : 'القسم الثاني'}
                </h2>
                {/* Completion */}
                <h3 style={{ color: '#6366f1', fontSize: 18, margin: '12px 0 6px 0' }}>أسئلة الإكمال</h3>
                {questions[sectionKey].completion?.map((q, idx) => (
                  <div key={idx} style={{ marginBottom: 8, padding: 8, background: '#f9f9f9', borderRadius: 6 }}>
                    <div style={{ fontWeight: 'bold' }}>{idx + 1}. {q.sentence}</div>
                    <div>إجابتك: <b style={{ color: results.answers[sectionKey].completion[idx] === results.corrects[sectionKey].completion[idx] ? '#22c55e' : '#ef4444' }}>{results.answers[sectionKey].completion[idx] || 'بدون إجابة'}</b></div>
                    <div>الإجابة الصحيحة: <b>{results.corrects[sectionKey].completion[idx]}</b></div>
                  </div>
                ))}
                {/* Rephrase */}
                <h3 style={{ color: '#6366f1', fontSize: 18, margin: '12px 0 6px 0' }}>أسئلة إعادة الصياغة</h3>
                {questions[sectionKey].rephrase?.map((q, idx) => (
                  <div key={idx} style={{ marginBottom: 8, padding: 8, background: '#f9f9f9', borderRadius: 6 }}>
                    <div style={{ fontWeight: 'bold' }}>{idx + 1}. {q.sentence}</div>
                    <div>إجابتك: <b style={{ color: results.answers[sectionKey].rephrase[idx] === results.corrects[sectionKey].rephrase[idx] ? '#22c55e' : '#ef4444' }}>{results.answers[sectionKey].rephrase[idx] || 'بدون إجابة'}</b></div>
                    <div>الإجابة الصحيحة: <b>{results.corrects[sectionKey].rephrase[idx]}</b></div>
                  </div>
                ))}
                {/* Articles */}
                <h3 style={{ color: '#6366f1', fontSize: 18, margin: '12px 0 6px 0' }}>فهم المقروء</h3>
                {questions[sectionKey].articles?.map((article, aIdx) => (
                  <div key={aIdx} style={{ marginBottom: 16, padding: 8, background: '#f3f4f6', borderRadius: 6 }}>
                    <div style={{ fontWeight: 'bold', marginBottom: 4 }}>المقال {aIdx + 1}: {article.article}</div>
                    {article.questions?.map((q, qIdx) => (
                      <div key={qIdx} style={{ marginBottom: 6, padding: 6, background: '#fff', borderRadius: 4 }}>
                        <div>{qIdx + 1}. {q.sentence}</div>
                        <div>إجابتك: <b style={{ color: results.answers[sectionKey].articles[aIdx].questions[qIdx] === results.corrects[sectionKey].articles[aIdx][qIdx] ? '#22c55e' : '#ef4444' }}>{results.answers[sectionKey].articles[aIdx].questions[qIdx] || 'بدون إجابة'}</b></div>
                        <div>الإجابة الصحيحة: <b>{results.corrects[sectionKey].articles[aIdx][qIdx]}</b></div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
            {/* Section 3 Essay */}
            <div style={{ marginTop: 32, textAlign: 'right' }}>
              <h2 style={{ color: '#1976d2', fontSize: 22, marginBottom: 8 }}>القسم الثالث: كتابة مقال</h2>
              <div style={{ marginBottom: 8, fontWeight: 'bold' }}>الموضوع:</div>
              <div style={{ background: '#f9f9f9', padding: 12, borderRadius: 6, marginBottom: 8 }}>{results.essayTopic}</div>
              <div style={{ marginBottom: 8, fontWeight: 'bold' }}>مقالك:</div>
              <div style={{ background: '#fffbe7', padding: 12, borderRadius: 6, minHeight: 80 }}>{results.answers.section3.essay || 'لم يتم كتابة مقال.'}</div>
              <div style={{ color: '#888', marginTop: 8, fontSize: 15 }}>سيتم تصحيح المقال يدويًا من قبل المعلم.</div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Add modal notification rendering before main return
  if (premiumModal) {
    return (
      <div style={{ direction: 'rtl', padding: '2rem', maxWidth: 800, margin: '0 auto', position: 'relative' }}>
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.25)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff',
            padding: '2.5rem 2rem',
            borderRadius: 12,
            boxShadow: '0 4px 32px rgba(0,0,0,0.18)',
            textAlign: 'center',
            minWidth: 320,
            fontSize: 22,
            color: '#1a237e',
            fontWeight: 'bold',
          }}>
            {premiumExamMessage}
            <br />
            <a href="/register" style={{ margin: '24px 8px 0 8px', background: '#22c55e', color: '#fff', borderRadius: 8, padding: '10px 32px', fontWeight: 'bold', fontSize: 18, textDecoration: 'none', display: 'inline-block' }}>اشترك الآن</a>
            <button onClick={() => setPremiumModal(false)} style={{ margin: '24px 8px 0 8px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 32px', fontWeight: 'bold', fontSize: 18, cursor: 'pointer', display: 'inline-block' }}>حسنًا</button>
          </div>
        </div>
      </div>
    );
  }

  // Render current section
  const currentSectionConfig = EXAM_CONFIG.sections[currentSection];
  const currentAnswers = answers[`section${currentSection + 1}`];
  const currentQuestions = questions[`section${currentSection + 1}`];

  return (
    <div style={{ direction: 'rtl', padding: '2rem', maxWidth: 1000, margin: '0 auto' }}>
      {/* Header */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ fontSize: 24, color: '#1a237e', fontWeight: 'bold', margin: 0 }}>
                {currentSectionConfig.name}
              </h2>
              <p style={{ color: '#666', margin: '0.5rem 0 0 0' }}>
                {currentSectionConfig.description}
              </p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <FaClock style={{ color: sectionTimer <= 60 ? '#ef4444' : '#f59e42' }} />
                <span style={{ 
                  fontSize: 24, 
                  fontWeight: 'bold',
                  color: sectionTimer <= 60 ? '#ef4444' : '#f59e42'
                }}>
                  {formatTime(sectionTimer)}
                </span>
              </div>
              <div style={{ fontSize: 14, color: '#666' }}>
                פרק {currentSection + 1} מתוך {getExamConfig().sections.length}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section End Message */}
      {sectionEndMessage && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.7)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff',
            padding: '2rem',
            borderRadius: 12,
            boxShadow: '0 4px 32px rgba(0,0,0,0.3)',
            textAlign: 'center',
            maxWidth: 400,
            margin: '1rem',
          }}>
            <div style={{ fontSize: 24, color: '#ef4444', marginBottom: '1rem' }}>
              ⏰
            </div>
            <div style={{ 
              fontSize: 18, 
              color: '#1a237e', 
              fontWeight: 'bold',
              marginBottom: '1rem',
              lineHeight: 1.5
            }}>
              {sectionEndMessage}
            </div>
            <div style={{ fontSize: 14, color: '#666' }}>
              يرجى الانتظار...
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          {loading ? (
            <div style={{ textAlign: 'center', color: '#888' }}>جاري التحميل...</div>
          ) : error ? (
            <div style={{ textAlign: 'center', color: 'red' }}>{error}</div>
          ) : (
            <div>
              {currentSection < 2 ? (
                // Sections 1 & 2: Completion, Rephrase, and Reading
                <div>
                  {/* Completion Questions */}
                  <div style={{ marginBottom: '3rem' }}>
                    <h3 style={{ color: '#1976d2', marginBottom: '1rem' }}>השלמת משפטים (8 שאלות)</h3>
                    {currentQuestions.completion?.map((question, index) => (
                      <div key={index} style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #eee', borderRadius: 8 }}>
                        <p style={{ marginBottom: '1rem', fontWeight: 'bold' }}>
                          {index + 1}. {question.sentence}
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {question.choices?.map((choice, choiceIndex) => (
                            <label key={choiceIndex} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <input
                                type="radio"
                                name={`completion-${index}`}
                                value={choice}
                                checked={currentAnswers.completion[index] === choice}
                                onChange={(e) => handleAnswerChange('completion', index, e.target.value)}
                              />
                              {choice}
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Rephrase Questions */}
                  <div style={{ marginBottom: '3rem' }}>
                    <h3 style={{ color: '#1976d2', marginBottom: '1rem' }}>ניסוח מחדש (5 שאלות)</h3>
                    {currentQuestions.rephrase?.map((question, index) => (
                      <div key={index} style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #eee', borderRadius: 8 }}>
                        <p style={{ marginBottom: '1rem', fontWeight: 'bold' }}>
                          {index + 1}. {question.sentence}
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {question.choices?.map((choice, choiceIndex) => (
                            <label key={choiceIndex} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <input
                                type="radio"
                                name={`rephrase-${index}`}
                                value={choice}
                                checked={currentAnswers.rephrase[index] === choice}
                                onChange={(e) => handleAnswerChange('rephrase', index, e.target.value)}
                              />
                              {choice}
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Reading Articles */}
                  <div>
                    <h3 style={{ color: '#1976d2', marginBottom: '1rem' }}>הבנת הנקרא (שני קטעים)</h3>
                    {currentQuestions.articles?.map((article, articleIndex) => (
                    <div key={articleIndex} style={{ marginBottom: '3rem', padding: '1rem', border: '1px solid #ddd', borderRadius: 8 }}>
                      <h4 style={{ color: '#333', marginBottom: '1rem' }}>הקטע {articleIndex + 1}</h4>
                      <div style={{ 
                        background: '#f9f9f9', 
                        padding: '1rem', 
                        borderRadius: 6, 
                        marginBottom: '1rem',
                        lineHeight: 1.6
                      }}>
                        {article.article.split('\n\n').map((paragraph, idx) => (
                          <p key={idx} style={{ margin: idx > 0 ? '12px 0 0 0' : '0 0 12px 0' }}>
                            {paragraph}
                          </p>
                        ))}
                      </div>
                      
                      {article.questions?.map((question, questionIndex) => (
                        <div key={questionIndex} style={{ marginBottom: '1.5rem' }}>
                          <p style={{ marginBottom: '0.5rem', fontWeight: 'bold' }}>
                            {questionIndex + 1}. {question.sentence}
                          </p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {question.choices?.map((choice, choiceIndex) => (
                              <label key={choiceIndex} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <input
                                  type="radio"
                                  name={`article-${articleIndex}-${questionIndex}`}
                                  value={choice}
                                  checked={currentAnswers.articles[articleIndex].questions[questionIndex] === choice}
                                  onChange={(e) => handleAnswerChange('article', `${articleIndex}-${questionIndex}`, e.target.value)}
                                />
                                {choice}
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
                </div>
              ) : (
                // Section 3: Essay
                <div>
                  <h3 style={{ color: '#1976d2', marginBottom: '1rem' }}>חיבור</h3>
                  {currentQuestions.essay && (
                    <div style={{ marginBottom: '2rem' }}>
                      <div style={{ 
                        background: '#f9f9f9', 
                        padding: '1rem', 
                        borderRadius: 6,
                        marginBottom: '1rem'
                      }}>
                        {currentQuestions.essay.topic}
                      </div>
                      
                      {/* Word count requirements */}
                      <div style={{ 
                        background: '#e3f2fd', 
                        padding: '0.75rem', 
                        borderRadius: 6,
                        marginBottom: '1rem',
                        border: '1px solid #bbdefb'
                      }}>
                        <div style={{ color: '#1976d2', fontSize: 14 }}>                    <ul style={{ margin: 0, paddingRight: 20, color: '#0369a1', fontSize: 15 }}>
                      <li>עליכם לכתוב על הנושא בהיקף של 120 עד 150 מילים.</li>
                      <li>שימו לב שהחיבור יתאים לנושא, והשתדלו לכתוב באופן מסודר ובלשון נכונה.</li>
                    </ul>
                        </div>
                      </div>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                          כתוב את החיבור שלך כאן:
                        </label>
                        <textarea
                          value={currentAnswers.essay}
                          onChange={(e) => handleAnswerChange('essay', null, e.target.value)}
                          rows={15}
                          style={{
                            width: '100%',
                            padding: '1rem',
                            border: '1px solid #ddd',
                            borderRadius: 6,
                            fontSize: 16,
                            lineHeight: 1.6,
                            resize: 'vertical'
                          }}
                          placeholder="התחל בכתיבת החיבור שלך כאן..."
                        />
                        
                        {/* Word count display */}
                        <div style={{ 
                          textAlign: 'right', 
                          fontSize: 14, 
                          color: '#666', 
                          marginTop: '0.5rem' 
                        }}>
                          מספר המילים: {currentAnswers.essay ? currentAnswers.essay.split(/\s+/).filter(word => word.length > 0).length : 0}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Footer with progress */}
      <div style={{ 
        marginTop: '2rem', 
        padding: '1rem', 
        background: '#f5f5f5', 
        borderRadius: 8,
        textAlign: 'center'
      }}>
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ 
            width: '100%', 
            height: 8, 
            background: '#e3e3e3', 
            borderRadius: 4, 
            overflow: 'hidden' 
          }}>
            <div style={{
              width: `${((currentSection + 1) / getExamConfig().sections.length) * 100}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #1976d2 60%, #22c55e 100%)',
              borderRadius: 4,
              transition: 'width 0.4s ease'
            }} />
          </div>
        </div>
        
        <div style={{ color: '#666', fontSize: 14 }}>
          התקדמות המבחן: {currentSection + 1} מתוך {getExamConfig().sections.length}
        </div>
      </div>
    </div>
  );
} 